import Dinero from "dinero.js";
import { CurrencySnapshot } from "./types";

export const pluralize = (
  count: number,
  singular: string,
  plural?: string
): string => {
  const pluralForm = plural || singular + "s";
  return count === 1 ? singular : pluralForm;
};

export const formatSatoshis = (
  satoshis: number,
  isPositive: boolean,
  singularPostfix: string
): string => {
  let prefix = isPositive ? "" : "-";
  let postfix = satoshis === 1 ? singularPostfix : singularPostfix + "s";
  return prefix + satoshis.toLocaleString() + " " + postfix;
};

export const formatFiat = ({
  satoshis,
  currency,
  currencySnapshot,
  isNegative = false,
  locale = navigator.language,
}: {
  satoshis: number | null;
  currency: string;
  currencySnapshot: CurrencySnapshot;
  isNegative?: boolean;
  locale?: string;
}) => {
  if (satoshis === null || satoshis === 0) return "";
  const lowerCurrency = currency.toLowerCase();
  const rate = currencySnapshot[lowerCurrency]; // This is fiat per BTC
  if (!rate) return "";
  const upperCurrency = currency.toUpperCase();
  const config = currencyConfig[upperCurrency];
  if (!config) return "";
  const { symbol, precision } = config;

  // rate is fiat per BTC, so: fiat = (satoshis / 100_000_000) * rate
  const btcAmount = satoshis / 100_000_000;
  const fiatAmount = btcAmount * rate;
  const absFiatMinor = Math.round(fiatAmount * 10 ** precision);

  // If the amount rounds to zero, show positive zero (not negative zero)
  const minor =
    absFiatMinor === 0 ? 0 : isNegative ? -absFiatMinor : absFiatMinor;

  const money = Dinero({ amount: Number(minor), precision });
  money.setLocale(locale);
  const decimalPart = precision > 0 ? "." + "0".repeat(precision) : "";
  const formatStr = "0,0" + decimalPart;
  let formatted = money.toFormat(formatStr);
  // Add symbol after sign
  if (formatted.startsWith("-")) {
    formatted = "-" + symbol + formatted.slice(1);
  } else {
    formatted = symbol + formatted;
  }
  return formatted;
};

export const currencyConfig: Record<
  string,
  { symbol: string; precision: number }
> = {
  AED: { symbol: "د.إ", precision: 2 },
  ARS: { symbol: "AR$", precision: 2 },
  AUD: { symbol: "A$", precision: 2 },
  BDT: { symbol: "৳", precision: 2 },
  BHD: { symbol: "د.ب", precision: 3 },
  BMD: { symbol: "$", precision: 2 },
  BRL: { symbol: "R$", precision: 2 },
  CAD: { symbol: "C$", precision: 2 },
  CHF: { symbol: "CHF", precision: 2 },
  CLP: { symbol: "$", precision: 0 },
  CNY: { symbol: "¥", precision: 2 },
  CZK: { symbol: "Kč", precision: 2 },
  DKK: { symbol: "kr.", precision: 2 },
  EUR: { symbol: "€", precision: 2 },
  GBP: { symbol: "£", precision: 2 },
  GEL: { symbol: "₾", precision: 2 },
  HKD: { symbol: "HK$", precision: 2 },
  HUF: { symbol: "Ft", precision: 2 },
  IDR: { symbol: "Rp", precision: 2 },
  ILS: { symbol: "₪", precision: 2 },
  INR: { symbol: "₹", precision: 2 },
  JPY: { symbol: "¥", precision: 0 },
  KRW: { symbol: "₩", precision: 0 },
  KWD: { symbol: "د.ك", precision: 3 },
  LKR: { symbol: "Rs", precision: 2 },
  MMK: { symbol: "Ks", precision: 2 },
  MXN: { symbol: "MX$", precision: 2 },
  MYR: { symbol: "RM", precision: 2 },
  NGN: { symbol: "₦", precision: 2 },
  NOK: { symbol: "kr", precision: 2 },
  NZD: { symbol: "NZ$", precision: 2 },
  PHP: { symbol: "₱", precision: 2 },
  PKR: { symbol: "₨", precision: 2 },
  PLN: { symbol: "zł", precision: 2 },
  RUB: { symbol: "₽", precision: 2 },
  SAR: { symbol: "ر.س", precision: 2 },
  SEK: { symbol: "kr", precision: 2 },
  SGD: { symbol: "S$", precision: 2 },
  THB: { symbol: "฿", precision: 2 },
  TRY: { symbol: "₺", precision: 2 },
  TWD: { symbol: "NT$", precision: 2 },
  UAH: { symbol: "₴", precision: 2 },
  USD: { symbol: "$", precision: 2 },
  VEF: { symbol: "Bs.", precision: 2 }, // legacy (deprecated)
  VES: { symbol: "Bs.", precision: 2 }, // current Venezuelan bolívar
  VND: { symbol: "₫", precision: 0 },
  ZAR: { symbol: "R", precision: 2 },
};

export const formatExchangeRate = ({
  currency,
  currencySnapshot,
  locale = navigator.language,
}: {
  currency: string;
  currencySnapshot: CurrencySnapshot;
  locale?: string;
}): string => {
  const { btcFirst, fiatFirst } = getExchangeRateParts({
    currency,
    currencySnapshot,
    locale,
  });
  if (!btcFirst || !fiatFirst) return "";
  return `${btcFirst} / ${fiatFirst}`;
};

export const getExchangeRateParts = ({
  currency,
  currencySnapshot,
  locale = navigator.language,
}: {
  currency: string;
  currencySnapshot: CurrencySnapshot;
  locale?: string;
}): { btcFirst: string; fiatFirst: string } => {
  const lowerCurrency = currency.toLowerCase();
  const rate = currencySnapshot[lowerCurrency]; // fiat per BTC
  if (!rate) return { btcFirst: "", fiatFirst: "" };

  const upperCurrency = currency.toUpperCase();
  const satoshisPerFiat = Math.round(100_000_000 / rate);

  const fiatFormatted = rate.toLocaleString(locale, {
    style: "currency",
    currency: upperCurrency,
  });

  return {
    btcFirst: `1 BTC = ${fiatFormatted}`,
    fiatFirst: `1 ${upperCurrency} = ${satoshisPerFiat.toLocaleString(
      locale
    )} ${pluralize(satoshisPerFiat, "sat")}`,
  };
};
